// PHẦN NHÌN CỦA MỘT ĐƠN TỰ MUA — dùng chung cho POPUP (vừa bấm mua) và TRANG (quay lại xem).
//
// Sếp Phú 28/08/2026 chốt luồng: chọn dịch vụ → hệ thống tạo đơn + sinh QR → **UI show popup**
// nội dung chuyển khoản → khách thanh toán → hệ nhận event tiền về và kích hoạt → **UI check
// trạng thái mỗi 30s** → thành công thì báo "giao dịch thành công".
//
// Popup và trang phải kể CÙNG MỘT câu chuyện. Viết hai bản là hai bản sẽ trôi khỏi nhau, và
// bản ít người mở hơn (trang) sẽ là bản sai — kiểu lỗi chỉ lộ khi khách quay lại xem đơn cũ.

import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Trans, useLingui } from '@lingui/react/macro';
import toast from 'react-hot-toast';
import {
  Loader2, Copy, CheckCircle2, Clock, XCircle, AlertTriangle, ArrowRight,
} from 'lucide-react';
import { marketplaceOrderApi, useUnitLabel } from './PlanPurchasePanel.jsx';
import { fmtNumber, fmtDateTime } from '../../i18n/format.js';
import { PRODUCT_NAME } from '../../lib/hostBrand';

/** Trạng thái đã ĐỨNG YÊN — ngừng hỏi lại máy chủ, không thì tab mở suốt ngày cứ nã mỗi 30 giây. */
export const TERMINAL = ['active', 'cancelled', 'failed'];

/**
 * Nhịp hỏi lại máy chủ.
 *
 * 30 giây là nhịp sếp chốt cho quãng CHỜ TIỀN VỀ — khách đang mở app ngân hàng, không ai nhìn
 * chằm chằm màn hình. Nhưng quãng CHỜ PHÁT HÀNH HOÁ ĐƠN thì khách vừa bấm mua xong và đang
 * nhìn con quay: 30 giây ở đó đọc thành "hỏng rồi", nên rút còn 3 giây. Cùng một con số cho cả
 * hai quãng là chọn sai một trong hai.
 */
function pollInterval(status) {
  if (TERMINAL.includes(status)) return false;
  return status === 'awaiting_invoice' ? 3000 : 30000;
}

export function useSelfOrder(id, { enabled = true } = {}) {
  return useQuery({
    queryKey: ['self-order', id],
    queryFn: () => marketplaceOrderApi.get(id),
    enabled: enabled && !!id,
    refetchInterval: (q) => pollInterval(q.state.data?.status),
    // Khách chuyển khoản bằng app ngân hàng rồi quay lại tab — cập nhật ngay lúc đó, đừng bắt
    // họ đợi hết nhịp 30 giây mới biết tiền đã về.
    refetchOnWindowFocus: true,
  });
}

/** Báo "giao dịch thành công" đúng MỘT lần, kể cả khi component vẽ lại nhiều lần. */
export function useNotifyWhenActive(status) {
  const { t } = useLingui();
  const done = useRef(false);
  useEffect(() => {
    if (status === 'active' && !done.current) {
      done.current = true;
      toast.success(t`Giao dịch thành công — gói đã kích hoạt cho workspace`);
    }
  }, [status, t]);
}

export function StatusBadge({ status }) {
  const { t } = useLingui();
  const cfg = {
    awaiting_invoice: { label: t`Đang tạo đơn`, cls: 'bg-gray-100 text-gray-600 border-gray-200', Icon: Loader2, spin: true },
    awaiting_payment: { label: t`Chờ chuyển khoản`, cls: 'bg-amber-50 text-amber-700 border-amber-200', Icon: Clock },
    paid: { label: t`Đã nhận tiền · đang cấp gói`, cls: 'bg-blue-50 text-blue-700 border-blue-200', Icon: Loader2, spin: true },
    provisioning: { label: t`Đang cấp gói`, cls: 'bg-blue-50 text-blue-700 border-blue-200', Icon: Loader2, spin: true },
    active: { label: t`Đã kích hoạt`, cls: 'bg-green-50 text-green-700 border-green-200', Icon: CheckCircle2 },
    cancelled: { label: t`Đã huỷ`, cls: 'bg-gray-100 text-gray-500 border-gray-200', Icon: XCircle },
    failed: { label: t`Không tạo được đơn`, cls: 'bg-red-50 text-red-600 border-red-200', Icon: AlertTriangle },
  }[status] || { label: status, cls: 'bg-gray-100 text-gray-600 border-gray-200', Icon: Clock };
  const { Icon } = cfg;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[12px] font-medium ${cfg.cls}`}>
      <Icon className={`w-3.5 h-3.5 ${cfg.spin ? 'animate-spin' : ''}`} /> {cfg.label}
    </span>
  );
}

function CopyRow({ label, value, mono = true }) {
  const { t } = useLingui();
  if (!value) return null;
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-gray-100 last:border-0">
      <span className="text-[13px] text-gray-500 shrink-0">{label}</span>
      <span className="flex items-center gap-2 min-w-0">
        <span className={`text-[13px] font-semibold text-gray-900 truncate ${mono ? 'font-mono' : ''}`}>{value}</span>
        <button
          type="button"
          title={t`Sao chép`}
          onClick={() => {
            navigator.clipboard?.writeText(String(value))
              .then(() => toast.success(t`Đã sao chép`))
              // Trình duyệt chặn clipboard (không phải HTTPS, hoặc người dùng từ chối) → nói
              // thật để khách còn tự bôi đen, đừng im lặng làm như đã chép xong.
              .catch(() => toast.error(t`Trình duyệt không cho sao chép — vui lòng chọn và chép tay`));
          }}
          className="shrink-0 p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100"
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
      </span>
    </div>
  );
}

/** Dòng tóm tắt "mua gì" — popup và trang cùng dùng. */
export function SelfOrderSummary({ order }) {
  const unitLabel = useUnitLabel();
  return (
    <div className="text-sm text-gray-500">
      <Trans>{order.quantity} {unitLabel(order.item_unit)} · {order.months} tháng</Trans>
      {order.pay_instructions?.invoice_number && (
        <> · <span className="font-mono">{order.pay_instructions.invoice_number}</span></>
      )}
    </div>
  );
}

/**
 * Thân đơn theo trạng thái. `compact` = đang nằm trong popup (bỏ nhật ký cho gọn khung).
 */
export function SelfOrderBody({ order, onGoSeats, compact = false }) {
  const { t } = useLingui();
  const pay = order.pay_instructions || {};

  return (
    <div className="space-y-4">
      {order.status === 'awaiting_invoice' && (
        <div className="rounded-xl border border-gray-200 p-6 text-center">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto mb-3" />
          <div className="text-sm font-medium text-gray-800"><Trans>Đang tạo đơn hàng</Trans></div>
          <div className="text-[13px] text-gray-500 mt-1">
            <Trans>Thường mất vài giây. Mã QR sẽ hiện ngay khi xong.</Trans>
          </div>
        </div>
      )}

      {order.status === 'awaiting_payment' && (
        <div className="rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-3 bg-gray-50 border-b border-gray-200">
            <div className="text-sm font-semibold text-gray-900"><Trans>Chuyển khoản để kích hoạt</Trans></div>
            <div className="text-[12.5px] text-gray-500 mt-0.5">
              <Trans>Giữ NGUYÊN nội dung chuyển khoản — hệ thống đối chiếu tự động bằng nội dung này.</Trans>
            </div>
          </div>

          <div className="p-5 grid gap-5 md:grid-cols-[200px_1fr]">
            {pay.qr_url && (
              <div className="shrink-0">
                <img
                  src={pay.qr_url}
                  alt={t`Mã QR chuyển khoản`}
                  className="w-full max-w-[200px] rounded-lg border border-gray-200"
                  // Ảnh QR do dịch vụ ngoài dựng. Tải hỏng thì ẨN ảnh chứ không để khung vỡ —
                  // số tài khoản + nội dung bên cạnh vẫn đủ để khách chuyển tay.
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
                <div className="text-[11.5px] text-gray-500 text-center mt-2">
                  <Trans>Quét bằng app ngân hàng — đã điền sẵn số tiền và nội dung</Trans>
                </div>
              </div>
            )}

            <div className="min-w-0">
              <div className="mb-3">
                <div className="text-[11px] uppercase tracking-wider text-gray-400"><Trans>Số tiền</Trans></div>
                <div className="text-2xl font-extrabold text-gray-900">
                  {fmtNumber(order.amount_expected)} <span className="text-base font-semibold text-gray-500">{order.currency}</span>
                </div>
              </div>
              <CopyRow label={t`Nội dung chuyển khoản`} value={pay.transfer_content} />
              <CopyRow label={t`Số tài khoản`} value={pay.account_number} />
              <CopyRow label={t`Ngân hàng`} value={pay.bank_name} mono={false} />
              <CopyRow label={t`Chủ tài khoản`} value={pay.account_name} mono={false} />
              {pay.due_date && (
                <div className="text-[12.5px] text-gray-500 mt-3">
                  <Trans>Hạn thanh toán: {pay.due_date}</Trans>
                </div>
              )}
            </div>
          </div>

          <div className="px-5 py-3 bg-amber-50 border-t border-amber-200 text-[12.5px] text-amber-800 flex items-center gap-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" />
            <Trans>Đang chờ tiền về — màn hình tự cập nhật, không cần gửi biên lai.</Trans>
          </div>
        </div>
      )}

      {['paid', 'provisioning'].includes(order.status) && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-5">
          <div className="flex items-start gap-3">
            <Loader2 className="w-5 h-5 text-blue-500 animate-spin shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="font-semibold text-blue-900"><Trans>Đã nhận đủ tiền</Trans></div>
              <div className="text-blue-700 mt-0.5">
                <Trans>Gói đang được cấp cho workspace. Màn hình sẽ tự báo khi xong.</Trans>
              </div>
            </div>
          </div>
        </div>
      )}

      {order.status === 'active' && (
        <div className="rounded-xl border border-green-200 bg-green-50 p-5">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
            <div className="text-sm flex-1 min-w-0">
              <div className="font-semibold text-green-900"><Trans>Giao dịch thành công — gói đã kích hoạt</Trans></div>
              <div className="text-green-800 mt-0.5">
                <Trans>Bước cuối: chọn ai trong workspace được dùng, quota sẽ trừ dần theo từng người.</Trans>
              </div>
              <button
                type="button"
                onClick={onGoSeats}
                className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700"
              >
                <Trans>Cấp chỗ cho nhân viên</Trans> <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {order.status === 'failed' && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-5 text-sm">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-red-900"><Trans>Không tạo được đơn hàng</Trans></div>
              <div className="text-red-700 mt-0.5">{order.last_error}</div>
              <div className="text-red-700 mt-2">
                <Trans>Chưa có khoản tiền nào bị trừ. Liên hệ đội {PRODUCT_NAME} để được hỗ trợ đặt lại.</Trans>
              </div>
            </div>
          </div>
        </div>
      )}

      {!compact && !!order.events?.length && (
        <div className="rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-3 bg-gray-50 border-b border-gray-200 text-sm font-semibold text-gray-900">
            <Trans>Diễn biến đơn hàng</Trans>
          </div>
          <ul className="divide-y divide-gray-100">
            {order.events.map((e) => (
              <li key={e.id} className="px-5 py-2.5 flex items-center justify-between gap-3 text-[13px]">
                <StatusBadge status={e.to_status} />
                <span className="text-gray-400 shrink-0">{fmtDateTime(e.created_at)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
